import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import {
  currentMonth,
  computeTenantStatus,
} from "../utils/format";
import { PhoneCall, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function BulkAiCallDialog({ open, onClose, tenants = [] }) {
  const { payments, initiateBulkAiCalls } = useData();
  const month = currentMonth();
  const [phase, setPhase] = useState("idle"); // idle | confirm | calling | done
  const [result, setResult] = useState(null);
  const [target, setTarget] = useState("all");
  const [excluded, setExcluded] = useState(new Set());

  useEffect(() => {
    if (!open) {
      setPhase("idle");
      setResult(null);
      setTarget("all");
      setExcluded(new Set());
    }
  }, [open]);

  const recipients = useMemo(() => {
    return tenants.filter((t) => {
      if (t.status !== "ACTIVE") return false;
      if (target === "all") return true;
      const s = computeTenantStatus(t, payments, month).toLowerCase();
      return s === target;
    });
  }, [tenants, target, payments, month]);

  const countsByStatus = useMemo(() => {
    const active = tenants.filter((t) => t.status === "ACTIVE");
    const out = { all: active.length, overdue: 0, partial: 0, pending: 0, paid: 0 };
    active.forEach((t) => {
      const s = computeTenantStatus(t, payments, month).toLowerCase();
      if (out[s] != null) out[s]++;
    });
    return out;
  }, [tenants, payments, month]);

  const selected = recipients.filter((t) => !excluded.has(t.id));

  const toggleOne = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (excluded.size === 0) {
      setExcluded(new Set(recipients.map((t) => t.id)));
    } else {
      setExcluded(new Set());
    }
  };

  const start = async () => {
    if (selected.length === 0) return;
    setPhase("calling");
    setResult(null);
    try {
      const tenantIds = selected.map((t) => t.id);
      const data = await initiateBulkAiCalls(tenantIds, "en");
      const queued = data.queued != null ? data.queued : (data.communications?.length || tenantIds.length);
      setResult({ queued, failed: 0, total: data.total || queued });
    } catch (err) {
      console.error("Bulk AI call failed:", err);
      setResult({ queued: 0, failed: selected.length, total: selected.length });
    }
    setPhase("done");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk AI call reminders"
      description={result ? "Call summary" : `The AI voice agent will call ${selected.length} tenant${selected.length === 1 ? "" : "s"}.`}
    >
      {phase === "idle" && (
        <div className="space-y-4">
          <div>
            <label className="label-base">Target</label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "all", l: "All active", c: countsByStatus.all },
                { v: "overdue", l: "Overdue", c: countsByStatus.overdue },
                { v: "partial", l: "Partial", c: countsByStatus.partial },
                { v: "pending", l: "Pending", c: countsByStatus.pending },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => { setTarget(o.v); setExcluded(new Set()); }}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150 ${
                    target === o.v
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {o.l} · {o.c}
                </button>
              ))}
            </div>
          </div>

          {recipients.length > 0 ? (
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Will call ({selected.length} of {recipients.length})
                </span>
                <button type="button" onClick={toggleAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                  {excluded.size === 0 ? "Deselect all" : "Select all"}
                </button>
              </div>
              <ul className="max-h-40 divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {recipients.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => toggleOne(t.id)}
                  >
                    <input
                      type="checkbox"
                      checked={!excluded.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                      className="checkbox-base"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="flex-1 min-w-0 truncate font-medium text-gray-900 dark:text-white">
                      {t.name}
                      {t.houseNumber && (
                        <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">· House {t.houseNumber}</span>
                      )}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">{t.phone || "no phone"}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-500 dark:text-gray-400">
              No recipients match this target.
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn-primary"
              onClick={() => setPhase("confirm")}
              disabled={selected.length === 0}
            >
              <PhoneCall className="h-4 w-4" />
              Start {selected.length} call{selected.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}

      {phase === "confirm" && (
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
            <strong>Are you sure?</strong> The AI agent will call{" "}
            <strong>{selected.length} tenant{selected.length === 1 ? "" : "s"}</strong>.
            Transcripts will be available after each call ends.
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setPhase("idle")}>
              Go back
            </button>
            <button className="btn-primary" onClick={start}>
              <PhoneCall className="h-4 w-4" />
              Confirm & start calls
            </button>
          </div>
        </div>
      )}

      {phase === "calling" && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            Initiating {selected.length} call{selected.length === 1 ? "" : "s"}…
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">This may take a moment.</div>
        </div>
      )}

      {phase === "done" && result && (
        <div className="space-y-4">
          {result.failed === 0 ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                  {result.queued} call{result.queued === 1 ? "" : "s"} queued
                </p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  The AI agent is placing calls in the background. Transcripts and summaries will appear in each tenant's history as calls complete.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
              <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">Calls could not be queued</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  Could not queue calls for {result.total} recipient{result.total === 1 ? "" : "s"}. Please check phone numbers and Vapi configuration.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
